import {combineLatest, merge, Observable, OperatorFunction} from 'rxjs';
import {debounceTime, filter, map, withLatestFrom}          from 'rxjs/operators';
import {TestScheduler}                                      from 'rxjs/testing';

import UiEventType            from '../../../../Ui/Shared/Constants/UiEventType';
import IUiEventsStreamPayload from '../../../../Ui/Shared/Interfaces/IUiEventsStreamPayload';
import Playback               from '../../Playback/Playback';
import Volume                 from '../../Volume/Volume';
import Frame                  from '../Frame';

const IDLE_DELAY_DURATION = 3000;

/**
 * Receives the `uiEvents$` stream as an input, and filters emissions
 * to only include `FRAME_MOUSE_LEAVE` and `FRAME_TAP` events.
 */

const filterUiEvents = (
    uiEvents$: Observable<IUiEventsStreamPayload>
): Observable<void> =>
    uiEvents$
        .pipe(
            filter(({type}) => (
                [
                    UiEventType.FRAME_MOUSE_LEAVE,
                    UiEventType.FRAME_TAP
                ].includes(type)
            )),
            map(() => void 0)
        );

/**
 * Creates an operator function that receives any observable, and returns
 * a tuple containing the latest values from the frame, playback and volume stores.
 */

const mapToLatestValuesFromStores = (
    frameStoreUpdate$: Observable<Frame>,
    playbackStoreUpdate$: Observable<Playback>,
    volumeStoreUpdate$: Observable<Volume>
): OperatorFunction<void, [Frame, Playback, Volume]> => (input: Observable<void>) =>
    input
        .pipe(
            withLatestFrom(combineLatest(frameStoreUpdate$, playbackStoreUpdate$, volumeStoreUpdate$)),
            map(([_, values]) => values)
        );

/**
 * Creates a stream that emits when:
 * - 3000ms has elapsed after an emission from the `active$` stream, with
 *   no further emissions
 * OR
 * - A `FRAME_MOUSE_LEAVE` or `FRAME_TAP` ui event occurs
 * AND
 * - The controls are active, the player is playing, and the volume slider is not active
 */

const createIdleStream = (
    active$: Observable<void>,
    uiEvents$: Observable<IUiEventsStreamPayload>,
    frameStoreUpdate$: Observable<Frame>,
    playbackStoreUpdate$: Observable<Playback>,
    volumeStoreUpdate$: Observable<Volume>,
    scheduler?: TestScheduler
): Observable<void> =>
    merge(
        active$.pipe(
            debounceTime(IDLE_DELAY_DURATION, scheduler)
        ),
        filterUiEvents(uiEvents$)
    )
        .pipe(
            mapToLatestValuesFromStores(frameStoreUpdate$, playbackStoreUpdate$, volumeStoreUpdate$),
            filter(([frameStoreValue, playbackStoreValue, volumeStoreValue]) => (
                frameStoreValue.areControlsActive &&
                playbackStoreValue.isPlaying &&
                !volumeStoreValue.isSliderActive
            )),
            map(() => void 0)
        );

export default createIdleStream;